import clsx from "clsx";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  CalendarClock,
  Check,
  CircleHelp,
  ClipboardCheck,
  FileText,
  FolderPlus,
  Laptop,
  Map as MapIcon,
  MessageSquarePlus,
  MonitorSmartphone,
  Mountain,
  Network,
  Send,
  Sparkles,
  Swords,
  Tablet,
  X,
} from "lucide-react";
import { type CSSProperties, Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ESCAPE, registerEscape } from "../components/escapeStack";
import { GenerexMark } from "../components/GenerexMark";
import { Button, IconButton, Kbd, SectionLabel, shortcut } from "../components/ui";
import { k, t } from "../i18n/i18n";
import { OBSTACLE_GREEN } from "../map/terrain/TerrainLayers";
import { symbolSvg } from "../map/units/symbols";
import { useUi, type ViewKey } from "../state/ui";
import { GuideDownloadButton } from "./guide";
import { useHelp } from "./helpStore";
import { motion, Motion, usePresence } from "../components/presence";

/** Prise en main (`HelpOnboardingView` de l'iPad, étoffée pour le web à la demande d'Alexis du 01/10/2026) :
 *  neuf étapes illustrées qui suivent le déroulé d'un exercice — créer le dossier, la carte, construire sa
 *  manœuvre (docs/14 §2.12), analyser, rédiger, conduire, partager, l'aide — chacune en trois points, vérifiés
 *  dans le code ; à la fin, créer son premier dossier ou ouvrir un dossier
 *  d'exemple. Elle s'ouvre d'elle-même à la première visite du poste, puis depuis l'accueil et le centre d'aide.
 *  Illustrations dessinées dans la page (aucune image à charger), aux couleurs de l'interface (jetons) ; animées
 *  doucement, immobiles quand le poste demande moins de mouvement. Fenêtre au plan 80 (R9), Échap par la pile
 *  partagée, au rang d'une fenêtre modale. */

export interface Step {
  /** Clé du titre court de la pastille, traduite à l'affichage. */
  chip: string;
  /** Vue que l'étape présente (entrée de la barre latérale) : le test de l'aide vérifie qu'elle existe. */
  view?: ViewKey;
  title: string;
  lead: string;
  points: string[];
  art: () => ReactNode;
}

/** Texte traduit dont les `{nom}` reçoivent des éléments (touche, mot en gras) : l'ordre des mots suit la langue. */
function rich(text: string, parts: Record<string, ReactNode>): ReactNode {
  return text.split(/\{(\w+)\}/).map((part, i) => (i % 2 ? <Fragment key={i}>{parts[part]}</Fragment> : part));
}

export const STEPS: Step[] = [
  {
    chip: k("Bienvenue"),
    title: k("Bienvenue dans Generex Web"),
    lead: k("Préparez et conduisez vos exercices tactiques, de la mission reçue à l'analyse après action."),
    points: [
      k("Un dispositif doctrinal placé sur la carte, de la compagnie au corps d'armée, et pour la rotation CENTAC."),
      k("Le dossier d'exercice complet : mise en ambiance, ordre d'opérations, incidents et évaluations."),
      k("En français ou en anglais, sur ordinateur comme sur tablette, sans rien installer."),
    ],
    art: () => <ArtWelcome />,
  },
  {
    chip: k("Créer"),
    view: "mission",
    title: k("Créer un dossier"),
    lead: k("Tout part des Données initiales : quelques choix, et le dispositif se place tout seul."),
    points: [
      k("Cinq étapes, une à la fois : le dossier et son niveau, la force amie et sa mission, le lieu (camp de manœuvre ou terrain libre), la date et la météo, l'ennemi ; « Continuer » mène de l'une à l'autre."),
      k("« Créer le dossier » : les unités apparaissent et la carte se cadre sur le dispositif."),
      k("Pas encore d'ordre de bataille à vous ? Ceux fournis avec Generex sont prêts, et l'éditeur crée les vôtres."),
    ],
    art: () => <ArtCreate />,
  },
  {
    chip: k("Carte"),
    view: "objects",
    title: k("La carte et le dispositif"),
    lead: k("Le dispositif se lit et se modifie directement sur la carte. Un clic droit (appui long au doigt) ou le bouton « Annotation » en bas y ajoute une annotation."),
    points: [
      k("T1, T2, T3 en bas de l'écran : les unités glissent d'un temps à l'autre avec leurs symboles de mission."),
      k("Glissez une unité pour la déplacer : le déplacement est mémorisé, et l'ordre se compose d'après la carte."),
      k("Zoomez : chaque unité laisse place à ses subordonnés, jusqu'aux sections. Un clic ouvre la fiche d'une unité ; les calques montrent forêts, routes, ponts et goulots."),
    ],
    art: () => <ArtMap />,
  },
  {
    chip: k("Manœuvre"),
    view: "manoeuvre",
    title: k("Construire sa manœuvre"),
    lead: k("Dans la vue Manœuvre, le dispositif doctrinal se retouche à la main, étape par étape."),
    points: [
      k("« Modifier le fuseau » : glissez un rond blanc ; le fuseau garde sa largeur, comparée à la plage doctrinale du niveau."),
      k("Glissez les LIMA et les lignes de la ZO le long du fuseau ; {annuler} annule une retouche, {echap} termine l'édition."),
      k("Un clic droit sur une unité ouvre son menu de mission ; les unités d'un ordre de bataille ennemi se glissent depuis la liste."),
    ],
    art: () => <ArtManoeuvre />,
  },
  {
    chip: k("Analyser"),
    view: "terrain",
    title: k("Analyser le terrain et l'ennemi"),
    lead: k("Avant d'écrire, comprenez le terrain et ce que l'ennemi peut faire."),
    points: [
      k("Analyse du terrain : analyse de la zone d'opérations, zone à étudier, axe le plus favorable."),
      k("Modes d'action ennemis ME1 et ME2, confrontés à votre manœuvre par la Simulation des ME."),
      k("Logistique : besoins en carburant et en munitions d'après la carte et la météo."),
    ],
    art: () => <ArtAnalyse />,
  },
  {
    chip: k("Rédiger"),
    view: "briefing",
    title: k("Rédiger le dossier d'exercice"),
    lead: k("Les documents se rédigent d'après la carte : vous relisez, vous ajustez, vous diffusez."),
    points: [
      k("Fiche pays et mise en ambiance rédigées par l'IA locale, rubrique par rubrique, modifiables."),
      k("Ordre d'opérations généré, retouché, versionné, validé et exporté en PDF ou en Word."),
      k("Transmettez l'OPO à l'échelon subordonné par un simple code : son dossier arrive tout prêt."),
    ],
    art: () => <ArtWrite />,
  },
  {
    chip: k("Conduire"),
    view: "timeline",
    title: k("Conduire l'exercice"),
    lead: k("Le jour J, la DIREX anime l'exercice et la 3A en tire les enseignements."),
    points: [
      k("DIREX : incidents MEL/MIL générés et datés, horloge d'exercice, injection programmée."),
      k("3A : notez chaque incident ; l'IA propose l'aide rédactionnelle, l'EVALOPS sort en PDF."),
      k("RETEX : versez vos comptes rendus, ils enrichissent les prochaines mises en ambiance."),
    ],
    art: () => <ArtConduct />,
  },
  {
    chip: k("Partager"),
    title: k("Travailler à plusieurs"),
    lead: k("Les dossiers vivent sur le serveur Generex, partagés entre les postes de votre licence."),
    points: [
      k("Reprenez un dossier depuis n'importe quel poste activé, ordinateur ou tablette."),
      k("Téléchargez un dossier au format .generex pour le garder ou le transmettre."),
      k("Interface en français ou en anglais (FR · EN) ; la langue des documents se choisit par dossier."),
    ],
    art: () => <ArtShare />,
  },
  {
    chip: k("Aide"),
    title: k("L'aide est partout"),
    lead: k("Une question en cours de route ? La réponse est à portée de clic. Une idée, une anomalie ? Le bouton « Retours » les transmet à l'équipe Generex."),
    points: [
      k("Le bouton {touche} de chaque vue ouvre l'aide de cet écran ; la touche {touche} aussi, à tout moment."),
      k("« Toute l'aide » rassemble toutes les fiches, le glossaire des sigles et, en tête, le guide de prise en main en PDF (« Guide PDF »)."),
      k("L'assistant IA répond à vos questions de doctrine et d'itinéraire, sources à l'appui."),
    ],
    art: () => <ArtHelp />,
  },
];

export function HelpOnboarding() {
  const open = useHelp((s) => s.onboarding);
  const { mounted, closing } = usePresence(open);
  if (!mounted) return null;
  return createPortal(<Tour closing={closing} />, document.body);
}

function Tour({ closing }: { closing: boolean }) {
  const close = useHelp((s) => s.closeOnboarding);
  const openHelp = useHelp((s) => s.openHelp);
  // Créer un dossier ou ouvrir l'exemple : seulement depuis l'accueil, qui s'en charge (fondu compris).
  const fromHome = useUi((s) => s.welcome);
  const [page, setPage] = useState(0);
  const primary = useRef<HTMLButtonElement>(null);
  const last = page === STEPS.length - 1;
  useEffect(() => {
    primary.current?.focus();
  }, [page]);
  // Échap : pile partagée (un appui ne ferme que la prise en main, au-dessus de tout le reste).
  useEffect(() => {
    if (closing) return;
    return registerEscape(ESCAPE.modal, () => close());
  }, [close, closing]);
  // Flèches : étape précédente ou suivante (pas pendant une saisie ni sur un contrôle qui s'en sert).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === "ArrowRight") setPage((p) => Math.min(p + 1, STEPS.length - 1));
      else if (e.key === "ArrowLeft") setPage((p) => Math.max(p - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const step = STEPS[Math.min(page, STEPS.length - 1)] as Step;
  const keys = { touche: <Kbd>?</Kbd>, annuler: <Kbd>{shortcut("Z")}</Kbd>, echap: <Kbd>{t("Échap")}</Kbd> };

  return (
    <div
      className={clsx("fixed inset-0 top-(--gx-strip-h) z-[80] flex items-center justify-center bg-ink/20 p-4", motion("fade", closing))}
      onClick={closing ? undefined : () => close()}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t("Prise en main")}
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          "relative flex max-h-full w-full max-w-[760px] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-float",
          motion("zoom", closing),
        )}
      >
        <header className="flex items-center gap-3 border-b border-line py-2 pr-2 pl-4">
          <ol className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto py-1" aria-label={t("Étapes")}>
            {STEPS.map((s, i) => (
              <li key={s.chip}>
                <button
                  type="button"
                  aria-current={i === page ? "step" : undefined}
                  onClick={() => setPage(i)}
                  title={t(s.title)}
                  className={clsx(
                    "inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-meta font-medium whitespace-nowrap transition-colors",
                    i === page ? "bg-accent text-white" : i < page ? "text-ink-soft hover:bg-hover" : "text-ink-muted hover:bg-hover",
                  )}
                >
                  {i < page ? <Check size={12} /> : <span className="tabular-nums">{i + 1}</span>}
                  <span className="max-md:hidden">{t(s.chip)}</span>
                </button>
              </li>
            ))}
          </ol>
          <IconButton size="sm" onClick={() => close()} title={t("Passer (Échap)")} aria-label={t("Passer (Échap)")}>
            <X size={16} />
          </IconButton>
        </header>

        {/* Hauteur constante d'une étape à l'autre : le pied (et « Suivant ») ne bouge pas sous le pointeur. */}
        <Motion key={page} origin="fade" className="grid min-h-0 flex-1 overflow-y-auto md:min-h-[324px] md:grid-cols-[300px_minmax(0,1fr)]">
          <div className="flex items-center justify-center bg-canvas px-6 py-8 max-md:py-6" aria-hidden>
            {step.art()}
          </div>
          <div className="px-7 py-7 max-md:px-5 max-md:py-5">
            <SectionLabel as="p" inset tone="accent" className="tabular-nums">
              {t("Prise en main")} · {t("Étape {n} sur {total}", { n: page + 1, total: STEPS.length })}
            </SectionLabel>
            <h2 className="mt-1.5 text-heading leading-snug font-semibold text-balance text-ink">{t(step.title)}</h2>
            <p className="mt-2.5 text-title leading-relaxed text-pretty text-ink-soft">{t(step.lead)}</p>
            <ul className="mt-5 space-y-3">
              {step.points.map((point) => (
                <li key={point} className="flex gap-3 text-ui leading-relaxed text-ink">
                  <span className="mt-[3px] inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span className="text-pretty">{rich(t(point), keys)}</span>
                </li>
              ))}
            </ul>
          </div>
        </Motion>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3.5">
          {page > 0 ? (
            <Button variant="ghost" onClick={() => setPage((p) => p - 1)}>
              <ArrowLeft size={14} /> {t("Précédent")}
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => close()}>
              {t("Passer")}
            </Button>
          )}
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {last ? (
              <>
                <GuideDownloadButton variant="ghost" label={t("Guide PDF")} />
                <Button
                  variant="ghost"
                  onClick={() => {
                    close();
                    openHelp({ tab: "all" });
                  }}
                >
                  {t("Toute l'aide")}
                </Button>
                {fromHome ? (
                  <>
                    <Button variant="secondary" onClick={() => close("example")}>
                      <Sparkles size={14} /> {t("Ouvrir un dossier d'exemple")}
                    </Button>
                    <Button ref={primary} onClick={() => close("create")}>
                      <FolderPlus size={14} /> {t("Créer mon premier dossier")}
                    </Button>
                  </>
                ) : (
                  <Button ref={primary} onClick={() => close()}>
                    {t("Commencer")}
                  </Button>
                )}
              </>
            ) : (
              <Button ref={primary} onClick={() => setPage((p) => p + 1)}>
                {t("Suivant")} <ArrowRight size={14} />
              </Button>
            )}
          </div>
        </footer>
      </section>
    </div>
  );
}

/* --- Illustrations ------------------------------------------------------------------------------------------ */

/* ui-check: ignore-start — illustrations de la prise en main, exemptées de l'échelle de texte et des coins (R1) :
   miniatures dessinées à leur propre échelle (étiquettes de 8 à 11 px, pastilles d'appareil). Couleurs en jetons neutres
   ou OTAN, jamais une couleur d'état en décor (R2). */

/** Symboles APP-6D des illustrations, dessinés comme ceux de la carte (fonds bleu ami et rouge ennemi de
 *  l'iPad) : compagnie d'infanterie amie, compagnie blindée ennemie. */
const SYMBOLS = { ami: "10031000151211000000", eni: "10061000151205000000" } as const;
const symbolUrls = new Map<string, string>();
function symbolUrl(side: keyof typeof SYMBOLS): string {
  let url = symbolUrls.get(side);
  if (!url) {
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(symbolSvg(SYMBOLS[side], 15))}`;
    symbolUrls.set(side, url);
  }
  return url;
}

function Unit({ side, className, style }: { side: keyof typeof SYMBOLS; className?: string; style?: CSSProperties }) {
  return <img src={symbolUrl(side)} alt="" draggable={false} className={clsx("absolute block max-w-none", className)} style={style} />;
}

/** Cadre d'une illustration : fond de carte (gris neutre) ou de document ; sa position vient de `className`. */
function Frame({ children, className, map = false }: { children: ReactNode; className?: string; map?: boolean }) {
  return (
    <div className={clsx("overflow-hidden rounded-xl border border-line shadow-sm", map ? "bg-canvas" : "bg-surface", className)}>{children}</div>
  );
}

function ArtWelcome() {
  const pillars: { icon: ReactNode; label: string }[] = [
    { icon: <MapIcon size={15} />, label: t("Carte") },
    { icon: <FileText size={15} />, label: t("Ordre") },
    { icon: <CalendarClock size={15} />, label: t("Conduite") },
  ];
  return (
    <div className="flex flex-col items-center gap-5">
      <div className="onboarding-float">
        <GenerexMark size={72} />
      </div>
      <div className="flex gap-2">
        {pillars.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11.5px] font-medium text-ink-soft shadow-sm">
            <span className="text-accent">{p.icon}</span> {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ArtCreate() {
  const fields = [t("Niveau"), t("Force"), t("Mission"), t("Camp")];
  return (
    <div className="flex items-center gap-3">
      <Frame className="w-[118px] space-y-1.5 p-2.5">
        {fields.map((f, i) => (
          <div key={f}>
            <div className="text-[9px] font-medium text-ink-muted">{f}</div>
            <div className={clsx("mt-0.5 h-2.5 rounded-sm", i === 3 ? "bg-accent/30" : "bg-line")} />
          </div>
        ))}
        <div className="mt-2 flex h-5 items-center justify-center rounded-md bg-ink text-[9px] font-semibold text-white">{t("Créer")}</div>
      </Frame>
      <ArrowRight size={18} className="shrink-0 text-ink-muted" />
      <Frame map className="relative h-[136px] w-[108px]">
        <span className="absolute inset-x-0 top-[54%] h-[1.5px] bg-app6-friend" />
        <Unit side="eni" className="onboarding-pop top-3 left-3" />
        <Unit side="eni" className="onboarding-pop top-7 right-3" style={{ animationDelay: "120ms" }} />
        <Unit side="ami" className="onboarding-pop bottom-9 left-2" style={{ animationDelay: "240ms" }} />
        <Unit side="ami" className="onboarding-pop right-2 bottom-10" style={{ animationDelay: "360ms" }} />
        <Unit side="ami" className="onboarding-pop bottom-2 left-[34px]" style={{ animationDelay: "480ms" }} />
      </Frame>
    </div>
  );
}

function ArtMap() {
  return (
    <div className="flex flex-col items-center gap-3">
      <Frame map className="relative h-[150px] w-[236px]">
        <span className="absolute -top-2 -bottom-2 left-[40%] w-[3px] rotate-[8deg] bg-line-strong" />
        {/* LIMA, au bleu des limites du fuseau sur la carte. */}
        <span className="absolute inset-x-0 top-[47%] h-[1.5px] bg-app6-friend" />
        <span className="absolute top-[47%] left-1.5 -translate-y-full pb-0.5 text-[8px] font-semibold text-friend">L1</span>
        <Unit side="eni" className="top-2.5 left-[30%]" />
        <Unit side="eni" className="top-4 right-[14%]" />
        {/* Une seconde d'avance sur le cycle : l'étape s'ouvre sur T1, unités en place. */}
        <Unit side="ami" className="onboarding-glide left-[10%]" style={{ animationDelay: "-1s" }} />
        <Unit side="ami" className="onboarding-glide left-[42%]" style={{ animationDelay: "-0.94s" }} />
        <Unit side="ami" className="onboarding-glide right-[8%]" style={{ animationDelay: "-0.88s" }} />
      </Frame>
      <div className="flex rounded-lg bg-surface p-0.5 shadow-sm ring-1 ring-line">
        {["T1", "T2", "T3"].map((p, i) => (
          <span key={p} className="onboarding-phase inline-flex h-6 w-9 items-center justify-center rounded-md text-[11px] font-semibold" style={{ animationDelay: `${i * 2 - 1}s` }}>
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

function ArtManoeuvre() {
  return (
    <div className="flex flex-col items-center gap-3">
      <Frame map className="relative h-[150px] w-[236px]">
        <svg viewBox="0 0 236 150" className="absolute inset-0">
          {/* Fuseau automatique en tirets estompés, fuseau retouché qui se dessine en pivotant vers la droite à largeur
              constante, au bleu ami des limites ; ronds blancs des sommets glissés, comme en mode édition. */}
          <path d="M70 146 L76 4 M166 146 L160 4" fill="none" stroke="var(--color-app6-friend)" strokeOpacity={0.4} strokeWidth="1.5" strokeDasharray="4 3" />
          <path className="onboarding-draw" pathLength={120} d="M70 146 L70 78 L100 4" fill="none" stroke="var(--color-app6-friend)" strokeWidth="2" strokeLinejoin="round" />
          <path className="onboarding-draw" pathLength={120} d="M166 146 L166 78 L196 4" fill="none" stroke="var(--color-app6-friend)" strokeWidth="2" strokeLinejoin="round" />
          <path d="M70 96 L166 96" fill="none" stroke="var(--color-app6-friend)" strokeWidth="1.5" />
          {[
            [70, 78],
            [166, 78],
          ].map(([cx, cy], i) => (
            <circle key={i} className="onboarding-pop" style={{ animationDelay: `${1.4 + i * 0.12}s`, transformOrigin: `${cx}px ${cy}px` }} cx={cx} cy={cy} r="4.5" fill="var(--color-surface)" stroke="var(--color-app6-friend)" strokeWidth="1.5" />
          ))}
        </svg>
        <span className="absolute top-[96px] left-[172px] -translate-y-1/2 text-[8px] font-semibold text-friend">L1</span>
        <Unit side="ami" className="onboarding-pop top-[108px] left-[108px]" style={{ animationDelay: "0.3s" }} />
        <Unit side="eni" className="top-4 left-[140px]" />
      </Frame>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-soft shadow-sm">
        <Check size={12} className="text-accent" /> {t("Garder la largeur")}
      </span>
    </div>
  );
}

function ArtAnalyse() {
  return (
    <div className="flex items-center gap-3">
      <Frame map className="relative h-[140px] w-[132px]">
        <svg viewBox="0 0 132 140" className="absolute inset-0">
          {/* Zone analysée, au vert OTAN des obstacles comme sur la carte. */}
          <path d="M18 110 L30 34 L92 22 L116 72 L84 120 Z" fill={OBSTACLE_GREEN} fillOpacity={0.18} stroke={OBSTACLE_GREEN} strokeWidth="1.5" strokeDasharray="4 3" />
          <path className="onboarding-draw" d="M34 104 C 52 80, 70 66, 96 40" fill="none" stroke="var(--color-accent)" strokeWidth="3" strokeLinecap="round" />
          <path d="M90 36 L100 37 L96 46" fill="none" stroke="var(--color-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <Mountain size={14} className="absolute top-2 right-2 text-ink-muted" />
      </Frame>
      <div className="space-y-2">
        {["ME1", "ME2"].map((me) => (
          <Frame key={me} className="w-[92px] p-2">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-hostile">
              <Swords size={11} /> {me}
            </div>
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-line" />
            <div className="mt-1 h-1.5 w-2/3 rounded-full bg-line" />
          </Frame>
        ))}
      </div>
    </div>
  );
}

function ArtWrite() {
  return (
    <div className="relative h-[156px] w-[200px]">
      <Frame className="absolute top-4 left-12 h-[132px] w-[104px] rotate-[6deg] p-3 opacity-70">
        <div className="space-y-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-1 rounded-full bg-line" style={{ width: `${90 - i * 9}%` }} />
          ))}
        </div>
      </Frame>
      <Frame className="absolute top-2 left-3 h-[136px] w-[108px] p-3">
        <div className="flex items-center gap-1 text-[9px] font-semibold text-run">
          <Sparkles size={10} /> {t("IA")}
        </div>
        <div className="mt-2 space-y-1.5">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="onboarding-type h-1 rounded-full bg-ink/15" style={{ width: `${95 - (i % 3) * 14}%`, animationDelay: `${i * 0.25}s` }} />
          ))}
        </div>
      </Frame>
      <span className="absolute right-3 bottom-6 inline-flex items-center gap-1 rounded-md bg-brand px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">PDF</span>
      <span className="absolute top-3 right-2 inline-flex size-8 items-center justify-center rounded-full bg-surface text-accent shadow-sm ring-1 ring-line">
        <Send size={14} />
      </span>
    </div>
  );
}

function ArtConduct() {
  const dots = [12, 26, 41, 58, 66, 83];
  return (
    <div className="w-[236px] space-y-3">
      <Frame className="p-3">
        <div className="flex justify-between text-[9.5px] font-semibold text-ink-muted">
          <span>T1</span>
          <span>T2</span>
          <span>T3</span>
        </div>
        <div className="relative mt-2 h-1.5 rounded-full bg-line">
          <span className="onboarding-clock absolute inset-y-0 left-0 rounded-full bg-accent/60" />
          {dots.map((left, i) => (
            <span key={left} className={clsx("absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full ring-2 ring-surface", i % 3 === 1 ? "bg-hostile" : "bg-run")} style={{ left: `${left}%` }} />
          ))}
        </div>
      </Frame>
      <Frame className="flex items-end gap-1.5 p-3">
        {[3, 4, 2, 5, 4].map((v, i) => (
          <span key={i} className="onboarding-bar w-full rounded-t-sm bg-accent/50" style={{ height: `${v * 9}px`, animationDelay: `${i * 0.1}s` }} />
        ))}
        <ClipboardCheck size={16} className="ml-1 shrink-0 text-ink-muted" />
      </Frame>
    </div>
  );
}

function ArtShare() {
  const devices = [<Laptop key="l" size={18} />, <Tablet key="t" size={18} />, <MonitorSmartphone key="m" size={18} />];
  return (
    <div className="flex flex-col items-center gap-3">
      <span className="inline-flex size-14 items-center justify-center rounded-2xl bg-ink text-white shadow-sm">
        <Network size={24} />
      </span>
      <svg width="200" height="26" viewBox="0 0 200 26" className="text-line-strong">
        <path d="M100 0 L32 26 M100 0 L100 26 M100 0 L168 26" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" className="onboarding-flow" />
      </svg>
      <div className="flex gap-8">
        {devices.map((d, i) => (
          <span key={i} className="inline-flex size-10 items-center justify-center rounded-xl bg-surface text-ink-soft shadow-sm ring-1 ring-line">
            {d}
          </span>
        ))}
      </div>
      <span className="mt-1 inline-flex rounded-md bg-surface p-0.5 text-[10.5px] font-semibold shadow-sm ring-1 ring-line">
        <span className="rounded px-1.5 py-0.5 text-ink">FR</span>
        <span className="rounded bg-selected px-1.5 py-0.5 text-ink">EN</span>
      </span>
    </div>
  );
}

function ArtHelp() {
  return (
    <div className="flex flex-col items-center gap-4">
      <span className="onboarding-float inline-flex size-16 items-center justify-center rounded-2xl border border-line-strong bg-surface text-[28px] font-semibold text-ink shadow-sm">?</span>
      <div className="flex gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-soft shadow-sm">
          <CircleHelp size={13} className="text-accent" /> {t("Aide")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-soft shadow-sm">
          <BookOpenText size={13} className="text-accent" /> {t("Glossaire")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-soft shadow-sm">
          <Sparkles size={13} className="text-run" /> {t("Assistant")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink-soft shadow-sm">
          <MessageSquarePlus size={13} className="text-accent" /> {t("Retours")}
        </span>
      </div>
    </div>
  );
}
/* ui-check: ignore-end */
